import { pipe } from "fp-ts/lib/function";
import { initializeSelectElements } from "../helpers/choices-helpers";
import { ApiService } from "../services/api-service";
import { IFavoritesService } from "../services/favorites/favorites-interface";
import { LanguageService } from "../services/language-service";
import {
  AppNavigationService,
  INavigationService,
  ModalNavigationService,
} from "../services/navigation-service";
import { IQueryService } from "../services/query-service";
import * as RX from "rxjs";
import { Failure } from "../helpers/failure";
import { elem, Option } from "fp-ts/lib/Option.js";
import { option } from "fp-ts";
import { App } from "./app";
import { UGExhibition } from "../models/exhibition";
import {
  ApiFilterPanel,
  ApiSearchView,
  navigationType,
  SearchViewParams,
} from "../models/searchView";
import { fromDomElement } from "../components/searchModule/searchModule";
import { getAttributeValue } from "../helpers/getAttribute";

export type ServiceSet = {
  languageService: LanguageService;
  apiService: ApiService;
  queryService: IQueryService;
  favoritesService: IFavoritesService;
};

export function initializeRootSearchView(
  serviceSet: ServiceSet,
  searchViewId: string,
  element: HTMLElement
) {
  // Extract attribute values from the element
  const maybeLimit = getAttributeValue(element, "data-ug-limit");
  const maybeOffset = getAttributeValue(element, "data-ug-offset");
  const maybeFilterQuery = getAttributeValue(element, "data-ug-filter-query");
  const maybeExhibition = getAttributeValue(element, "data-ug-exhibition");
  const maybeClickOnElement = getAttributeValue(
    element,
    "data-ug-click-on-element"
  );

  // Convert Option<string> to a numeric value with a default value
  const toNumberWithDefault =
    (defaultValue: number) =>
    (maybeValue: Option<string>): number =>
      option.isSome(maybeValue) ? Number(maybeValue.value) : defaultValue;

  // Convert Option<string> to a string with a default value
  const toStringWithDefault =
    (defaultValue: string) =>
    (maybeValue: Option<string>): string =>
      option.isSome(maybeValue) ? maybeValue.value : defaultValue;

  // Create searchViewParams using the defined functions
  const searchViewParams = {
    exhibitionId: toNumberWithDefault(0)(maybeExhibition),
    limit: toNumberWithDefault(0)(maybeLimit),
    offset: toNumberWithDefault(0)(maybeOffset),
    filterQuery: toStringWithDefault("")(maybeFilterQuery),
  };

  const searchViewAndExhibition$ = (() => {
    const exhibitionId: number | undefined = pipe(
      getAttributeValue(element, "data-ug-exhibition"), // Option<string>
      option.map((value: string): number => Number(value)),
      option.filter((num: number): boolean => Number.isFinite(num)),
      option.getOrElse((): number | undefined => undefined)
    );

    return serviceSet.apiService.getSearchView(searchViewId, exhibitionId).pipe(
      RX.switchMap((searchView) => {
        searchView.elementId = element.id;
        if (exhibitionId !== undefined) {
          searchView.exhibitionId = exhibitionId;
        }

        const queryParams = new URLSearchParams(window.location.search);
        let exhibitionSlug = null;

        if (searchView.exhibitionQueryParameterKey) {
          exhibitionSlug = queryParams.get(
            searchView.exhibitionQueryParameterKey
          );
        }

        if (exhibitionSlug) {
          return App.getExhibitionFromSlug(exhibitionSlug).pipe(
            RX.map((exhibition) => {
              searchView.exhibitionId = exhibition.id;
              return { searchView, exhibition };
            })
          );
        }

        return App.getExhibitionFromId(searchView.exhibitionId).pipe(
          RX.map((exhibition) => ({ searchView, exhibition }))
        );
      })
    );
  })();

  searchViewAndExhibition$
    .pipe(
      RX.tap(({ exhibition, searchView }) => {
        if (searchView.exhibitionQueryParameterKey) {
          searchView.templateOptions.exhibitionQueryParameterKey = `?${searchView.exhibitionQueryParameterKey}=${exhibition.slug}`;
        }
        const filterQueryFromContext = searchViewParams.filterQuery;
        if (filterQueryFromContext) {
          if (searchView.elementFilterPage) {
            searchView.elementFilterPage += `#${filterQueryFromContext}`;
          } else {
            searchView.elementFilterPage = filterQueryFromContext;
          }
          if (searchView.elementFilterMap) {
            searchView.elementFilterMap += `#${filterQueryFromContext}`;
          } else {
            searchView.elementFilterMap = filterQueryFromContext;
          }
        }
        element.classList.add(searchView.class);
      }),
      RX.switchMap(({ exhibition, searchView }) => {
        // Override navigationTypePage if data-ug-click-on-element is set
        if (option.isSome(maybeClickOnElement)) {
          const clickValue = maybeClickOnElement.value as navigationType;
          searchView.navigationTypePage = clickValue;
          searchView.templateOptions.navigationTypePage = clickValue;
        }

        const appNavigationService = new AppNavigationService(
          element,
          searchView.id,
          searchView.breadcrumbIcons
        );
        const modalNavigationService = new ModalNavigationService(element);
        const navigationServicePage = SelectNavigationService(
          searchView.navigationTypePage,
          appNavigationService,
          modalNavigationService
        );
        const navigationServiceMap = SelectNavigationService(
          searchView.navigationTypeMap,
          appNavigationService,
          modalNavigationService
        );

        return serviceSet.apiService
          .getFilterPanel(searchView.exhibitionId, searchView.id)
          .pipe(
            RX.map((filterPanel) => ({
              exhibition,
              searchView,
              filterPanel,
              navigationServicePage,
              navigationServiceMap,
            }))
          );
      })
    )
    .subscribe({
      next: ({
        exhibition,
        searchView,
        filterPanel,
        navigationServicePage,
        navigationServiceMap,
      }) => {
        const container = navigationServicePage.getContent();
        container.innerHTML =
          filterPanel.filterPanelExhibitionCss +
          filterPanel.filterPanelSearchViewCss +
          filterPanel.filterPanelHtml;

        renderSearchView(
          exhibition,
          container,
          searchView,
          filterPanel,
          searchViewParams,
          navigationServicePage,
          navigationServiceMap,
          serviceSet
        );
      },
      error: (err) => {
        console.error("Error:", err);
      },
    });
}

export function renderSearchView(
  exhibition: UGExhibition,
  container: HTMLElement,
  searchView: ApiSearchView,
  filterPanel: ApiFilterPanel,
  searchViewParams: SearchViewParams,
  navigationServicePage: INavigationService,
  navigationServiceMap: INavigationService,
  serviceSet: ServiceSet
) {
  const searchModuleElement = pipe(
    container.querySelector<HTMLElement>(".ug.interactive-element-view"),
    option.fromNullable,
    option.getOrElseW(() => Failure("Missing searchModule element"))
  );

  initializeSelectElements(serviceSet.languageService, searchModuleElement);

  fromDomElement(
    searchModuleElement,
    searchView,
    searchViewParams,
    filterPanel,
    exhibition,
    serviceSet.languageService,
    serviceSet.queryService,
    serviceSet.apiService,
    serviceSet.favoritesService,
    navigationServicePage,
    navigationServiceMap
  );
}

function SelectNavigationService(
  navigationType: navigationType,
  appNavService: AppNavigationService,
  modalNavService: ModalNavigationService
) {
  if (navigationType == "modal") return modalNavService;
  return appNavService;
}
